/**
 * EVENT CARD
 * 
 * Display component for individual events in list view.
 * Location: src/components/events/EventCard.tsx
 */

import { ExternalLink, Clock, Tag, Sparkles, CalendarPlus, Repeat, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CalendarEvent } from '../../types/event';
import {
  getEventTypeInfo,
  formatEventTime,
  isEventLive,
  downloadEventICS
} from '../../types/event';

interface EventCardProps {
  event: CalendarEvent;
  compact?: boolean;
  onSetReminder?: (event: CalendarEvent) => void;
  onClick?: (event: CalendarEvent) => void;
}

export default function EventCard({ event, compact = false, onSetReminder, onClick }: EventCardProps) {
  const typeInfo = getEventTypeInfo(event.eventType);
  const timeStr = formatEventTime(event);
  const isLive = isEventLive(event);
  
  if (compact) {
    // Compact version for calendar day popover
    return (
      <button
        onClick={() => onClick?.(event)}
        className="w-full text-left p-2 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 transition-default cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${typeInfo.color}`} />
          <span className="text-sm font-medium text-glass truncate flex-1">
            {event.title}
          </span>
          {isLive && (
            <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded animate-pulse">
              LIVE
            </span>
          )}
        </div>
        <p className="text-xs text-glass-secondary mt-1 truncate">
          {event.brandName}
        </p>
      </button>
    );
  }

  // Full card version for list view
  return (
    <div className="glass-card p-4 hover:border-pink-500/30 transition-default group">
      <div className="flex gap-4">
        {/* Date badge */}
        <div className="flex-shrink-0 w-16 text-center">
          <div className={`rounded-lg p-2 ${typeInfo.color} bg-opacity-20`}>
            <div className={`text-2xl font-bold ${typeInfo.textColor}`}>
              {event.startDate.toDate().getDate()}
            </div>
            <div className="text-xs text-glass-secondary uppercase">
              {event.startDate.toDate().toLocaleDateString('en-US', { month: 'short' })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              {/* Type badge + Live indicator */}
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color} text-white`}>
                  {typeInfo.emoji} {typeInfo.label}
                </span>
                {isLive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse">
                    🔴 LIVE NOW
                  </span>
                )}
              </div>
              
              {/* Title - clickable to view details */}
              {onClick ? (
                <button 
                  onClick={() => onClick(event)}
                  className="text-lg font-bold text-glass group-hover:text-pink-300 transition-default text-left hover:underline"
                >
                  {event.title}
                </button>
              ) : (
                <h3 className="text-lg font-bold text-glass group-hover:text-pink-300 transition-default">
                  {event.title}
                </h3>
              )}
            </div>
          </div>

          {/* Brand */}
          <Link 
            to={`/brands/${event.brandSlug}`}
            className="inline-flex items-center gap-2 text-sm text-glass-secondary hover:text-pink-300 transition-default mb-2"
          >
            {event.brandLogo ? (
              <img 
                src={event.brandLogo} 
                alt="" 
                className="w-5 h-5 rounded-full object-cover"
              />
            ) : (
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {event.brandName.charAt(0)}
                </span>
              </div>
            )}
            {event.brandName}
          </Link>

          {/* Description */}
          {event.description && (
            <p className="text-sm text-glass-secondary mb-3 line-clamp-2">
              {event.description}
            </p>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-glass-secondary">
            {/* Time */}
            {timeStr && (
              <span className="flex items-center gap-1">
                <Clock className="icon-sm" />
                {timeStr}
              </span>
            )}
            
            {/* Recurring indicator */}
            {event.recurrence && event.recurrence.pattern !== 'none' && (
              <span className="flex items-center gap-1 text-purple-400">
                <Repeat className="icon-sm" />
                {event.recurrence.pattern === 'weekly' ? 'Weekly' : 'Monthly'}
              </span>
            )}
            
            {/* Discount info */}
            {event.discountInfo && (
              <span className="flex items-center gap-1 text-green-400">
                <Tag className="icon-sm" />
                {event.discountInfo}
              </span>
            )}
          </div>

          {/* Products teased */}
          {event.productsTeased && event.productsTeased.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {event.productsTeased.slice(0, 3).map((product, i) => (
                <span 
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700/50 rounded-full text-xs text-glass-secondary"
                >
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  {product}
                </span>
              ))}
              {event.productsTeased.length > 3 && (
                <span className="px-2 py-1 text-xs text-glass-muted">
                  +{event.productsTeased.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex flex-wrap gap-2">
            {/* Add to Calendar button */}
            <button
              onClick={() => downloadEventICS(event)}
              className="btn-secondary text-sm py-2 px-4 flex items-center gap-1"
              title="Add to your calendar"
            >
              <CalendarPlus className="icon-sm" />
              Add to Calendar
            </button>
            
            {/* Remind Me button */}
            {onSetReminder && (
              <button
                onClick={() => onSetReminder(event)}
                className="btn-secondary text-sm py-2 px-4 flex items-center gap-1"
                title="Set a reminder"
              >
                <Bell className="icon-sm" />
                Remind Me
              </button>
            )}
            
            {event.shopUrl && (
              <a
                href={event.shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm py-2 px-4 flex items-center gap-1"
              >
                Shop Now
                <ExternalLink className="icon-sm" />
              </a>
            )}
            {event.socialUrl && (
              <a
                href={event.socialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm py-2 px-4 flex items-center gap-1"
              >
                {event.eventType === 'live_sale' ? 'Join Live' : 'View'}
                <ExternalLink className="icon-sm" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mini event dot for calendar grid
 */
export function EventDot({ event }: { event: CalendarEvent }) {
  const typeInfo = getEventTypeInfo(event.eventType);
  
  return (
    <div 
      className={`w-2 h-2 rounded-full ${typeInfo.color}`}
      title={`${event.title} (${event.brandName})`}
    />
  );
}
